import React, { useEffect, useRef } from "react";

import { IconExternalLink } from "@frontend/common";

import { useNewInfoReviewRequiredMutation } from "@api/articleInfo/useArticleInfo";
import { NewInfoPressData } from "@api/dealsSimilarData/dealsSimilarData.type";

import * as S from "./NewInfoPressTableRow.style";
import Checkbox from "../../../OverlappedCheckBox/OverlappedCheckBox";
import { moveToFillInTable } from "../../utils/moveToFillInTable";

type Props = {
  newInfoPressItem: NewInfoPressData;
  hoveredRow: number | null;
  setHoveredRow: (hoveredRow: number | null) => void;
  checkedPressIds: Set<number>;
  onTogglePressId: (pressId: number) => void;
  onInitCheck: (pressId: number) => void;
  currentPressId: number;
  reviewInformationList: {
    id: number;
    reviewId: number;
    deleted: boolean;
  }[];
};

const NewInfoPressTableRow = ({
  newInfoPressItem,
  hoveredRow,
  setHoveredRow,
  checkedPressIds,
  onTogglePressId,
  onInitCheck,
  currentPressId,
  reviewInformationList,
}: Props) => {
  const { mutateAsync: updateNewInfoReviewRequiredStatus } =
    useNewInfoReviewRequiredMutation();

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (!reviewInformationList) return;
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const matchedReview = reviewInformationList.find(
      (item) => Number(item.id) === newInfoPressItem.id,
    );

    if (matchedReview?.deleted) {
      onInitCheck(newInfoPressItem.id);
    }
  }, [onInitCheck, newInfoPressItem.id, reviewInformationList]);

  const handleCheckboxChange = async () => {
    await updateNewInfoReviewRequiredStatus({
      pressId: currentPressId,
      requiredReviewInformationList: reviewInformationList.map((item) => {
        if (item.id === newInfoPressItem.id) {
          return {
            ...item,
            deleted: !item.deleted,
          };
        }
        return item;
      }),
    });
    onTogglePressId(newInfoPressItem.id);
  };

  const isChecked = !checkedPressIds.has(newInfoPressItem.id);
  return (
    <S.NewInfoPressTableBody
      key={newInfoPressItem.id}
      onMouseEnter={() => setHoveredRow(newInfoPressItem.id)}
      onMouseLeave={() => setHoveredRow(null)}
      hovered={hoveredRow === newInfoPressItem.id}
      checked={!isChecked}
    >
      <S.NewInfoPressTablePressIdWrapper
        onClick={() => moveToFillInTable(`${newInfoPressItem.pressId}`)}
      >
        <S.NewInfoPressId>#{newInfoPressItem.pressId}</S.NewInfoPressId>
        <S.NewInfoPressIcon>
          <IconExternalLink
            width={20}
            height={20}
            fill={!isChecked ? "#7c899c" : "#2A2F37"}
          />
        </S.NewInfoPressIcon>
      </S.NewInfoPressTablePressIdWrapper>

      <S.NewInfoPressTablePressTitleData>
        <S.NewInfoPressTablePressTitleOverflow>
          {newInfoPressItem.newInfoDetail || "-"}
        </S.NewInfoPressTablePressTitleOverflow>
      </S.NewInfoPressTablePressTitleData>
      <S.NewInfoPressTableReviewRequiredData>
        <div>
          <S.CheckBoxContainer>
            <Checkbox
              onChangeHandler={handleCheckboxChange}
              value={newInfoPressItem.id}
              checked={checkedPressIds.has(newInfoPressItem.id)}
            />
          </S.CheckBoxContainer>
        </div>
      </S.NewInfoPressTableReviewRequiredData>
    </S.NewInfoPressTableBody>
  );
};

export default NewInfoPressTableRow;
